import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  cn,
} from '@design-system';
import { ListFilter } from 'lucide-react';

interface FilterButtonProps<T> {
  values: T[];
  selectedValues: T[];
  onChange: (values: T[]) => void;
  renderValue?: (value: T) => string;
  getKey?: (value: T) => string;
}

export function FilterButton<T>({
  values,
  selectedValues,
  onChange,
  renderValue = v => (v == null ? 'Undefined' : String(v)),
  getKey = v => (v == null ? '__none__' : String(v)),
}: FilterButtonProps<T>) {
  const selectedKeys = new Set(selectedValues.map(getKey));

  const handleToggle = (value: T) => {
    const key = getKey(value);
    const next = selectedKeys.has(key)
      ? selectedValues.filter(v => getKey(v) !== key)
      : [...selectedValues, value];
    onChange(next);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <button
          className={cn(
            'text-muted-foreground hover:text-foreground inline-flex items-center justify-center rounded p-0.5',
            {
              'text-primary bg-primary/20': selectedKeys.size > 0,
            },
          )}
        >
          <ListFilter className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-64 overflow-y-auto"
      >
        {values.map(value => (
          <DropdownMenuCheckboxItem
            key={getKey(value)}
            checked={selectedKeys.has(getKey(value))}
            onCheckedChange={() => handleToggle(value)}
            onSelect={e => e.preventDefault()}
          >
            <span className="capitalize">{renderValue(value)}</span>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
